import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@shared/api/client';
import type { BlockTree } from '@shared/block-renderer';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { Alert } from '@shared/ui/Alert';
import { SurfaceError } from '@shared/ui/SurfaceError';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { fromBodyHtml, toBodyHtml } from '../_shared/body-html';
import { FormActions, PageHead, PageIdentity, PagesStrip, PageSection } from '../_shared/components';
import { sharedCopy } from '../_shared/copy';
import { pagesErrors } from '../_shared/errors';
import { useUpdateInformationalPage } from '../_shared/mutations';
import { informationalPageKey, useInformationalPage } from '../_shared/queries';

import { AboutBlockEditor } from './About.BlockEditor';
import { AboutSkeleton } from './About.Skeleton';
import { copy } from './copy';

/**
 * `/pages/about` — the operator side of `[x]` §20.17 over `[x]` §21.1: the body
 * of the public About Us page, in the three parts the row names and no fourth.
 *
 * **The title and the public path are facts, not fields.** §20.17 grants the
 * body content; the two informational pages are two named surfaces rather than a
 * collection that grows, so there is no create, no delete and no rename — and a
 * title field would imply all three.
 *
 * What this surface deliberately does not draw, each because no `[x]` row names
 * it: a **visibility or publish control** (the architecture models the page with
 * no scheduling and an on-or-off visibility, but taking a control from an
 * architecture note is the mockup inventing scope), a **«view the public page»
 * action** (the footer already links it from every page in this console, and two
 * controls for one destination is one too many), a **preview pane** (a second
 * renderer here would disagree with the public one unnoticed), a **last-edited
 * stamp or editor name** (`[ ]` §20.21's audit log is the row that would carry
 * «who changed what», and it is deferred), an **inline toolbar** for emphasis and
 * links, and an **artwork field** (§5 scopes artwork to catalogue items, §13.3 to
 * editorial posts, and §21.1 names none here).
 */
export function AboutPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const liveRegion = useRef<HTMLParagraphElement>(null);

  const page = useInformationalPage('about');
  const save = useUpdateInformationalPage('about');

  const [blocks, setBlocks] = useState<BlockTree>([]);
  const [failure, setFailure] = useState<string | null>(null);

  const record = page.data;

  /**
   * The stored body, parsed once per record rather than on every render — and
   * `unsupported` is read rather than assumed. A body carrying structure these
   * three kinds cannot hold would lose it on the first save, so the editor does
   * not open over it at all.
   */
  const parsed = useMemo(() => fromBodyHtml(record?.bodyHtml), [record?.bodyHtml]);

  // The record arrives after first paint on a cold load, and a body written from
  // another session must not be overwritten by a stale default.
  //
  // **THIS EFFECT NO LONGER TOUCHES THE SAVE CONFIRMATION, AND THAT IS THE FIX.**
  // A successful save invalidates this query; the refetch returns the body that
  // was just written, `record.bodyHtml` differs from the previous string, the
  // memo above recomputes into a NEW `parsed` object, and this effect re-runs —
  // and it used to call `setSaved(false)`, tearing the confirmation down tens of
  // milliseconds after it appeared. Saving an unchanged body left `bodyHtml`
  // byte-identical, the memo held its object, and the message stood: the «first
  // time it flashes, second time it is fine» of the report. The confirmation is
  // a toast now and lives outside this subtree, so no refetch can reach it.
  useEffect(() => {
    setBlocks(parsed.blocks);
    setFailure(null);
  }, [record?.id, parsed]);

  function announce(message: string) {
    if (liveRegion.current) liveRegion.current.textContent = message;
  }

  function reset() {
    setBlocks(parsed.blocks);
    // A failure standing over fields that were just restored says something that
    // is no longer true.
    setFailure(null);
  }

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFailure(null);

    save.mutate(
      // **`bodyFields` is not sent, and must not be.** The owning service accepts
      // that key on the `contact` slug alone — `_assert_contact_shape` refuses it
      // outright for any other page — so the `{ lede }` this form used to attach
      // was a second, independent reason every About save failed, waiting behind
      // the sanitiser 500 that masked it. The lede itself had no column and no
      // reader: `frontend-landing`'s About fetcher hardcodes `lede: null` beside a
      // note saying no field carries one.
      { bodyHtml: toBodyHtml(blocks) },
      {
        onSuccess: () => toast.show(copy.saved.body, 'success'),
        onError: (error) => {
          const status = error instanceof ApiError ? error.status : 0;
          const code = error instanceof ApiError ? error.code : undefined;
          setFailure(resolveErrorCopy(code, status, pagesErrors).message);
        },
      },
    );
  }

  if (page.isError) {
    return (
      <>
        <PageHead crumb={copy.crumb} title={copy.head.title} lede={copy.head.lede} />
        <PagesStrip current="about" />
        <SurfaceError
          error={page.error}
          catalog={pagesErrors}
          onRetry={() => void queryClient.invalidateQueries({ queryKey: informationalPageKey('about') })}
        />
      </>
    );
  }

  return (
    <>
      <PageHead crumb={copy.crumb} title={copy.head.title} lede={copy.head.lede} />
      <PagesStrip current="about" />

      {/*
        One polite region per surface, in the markup from load. Block moves and
        removals are progress, not errors, so it never interrupts.
      */}
      <p ref={liveRegion} className="sr-only" aria-live="polite" />

      {page.isPending ? (
        <AboutSkeleton />
      ) : (
        <form noValidate onSubmit={submit}>
          {/*
            THE REFUSAL SITS ABOVE THE FORM, NOT UNDER IT — the block editor is
            several screens tall and the one message an operator has to act on
            was the element furthest from the control that produced it.
            `<Alert kind="danger">` sets `role="alert"` itself; this wrapper is
            spacing only, and a second alert region around it was announced twice
            and drew two identical banners.

            It is NOT a toast: a failure the operator has to correct must not
            disappear on a timer while they are reading it. The success
            confirmation is the toast, because nothing follows from it.
          */}
          {failure ? (
            <div className="mb-6">
              <Alert kind="danger">{failure}</Alert>
            </div>
          ) : null}

          <PageSection id="pg-identity" heading={sharedCopy.identity.heading}>
            <PageIdentity
              slug="about"
              title={record?.title ?? copy.head.title}
              bodyShape={copy.identity.bodyShape}
            />
          </PageSection>

          <PageSection id="pg-body" heading={copy.body.heading}>
            {parsed.unsupported ? (
              <Alert kind="warning" title={copy.unsupported.title}>
                {copy.unsupported.body}
              </Alert>
            ) : (
              <>
                <p className="mb-4 mt-0 text-xs text-ink-muted">{copy.body.note}</p>

                <AboutBlockEditor
                  blocks={blocks}
                  onChange={setBlocks}
                  announce={announce}
                  noteAfterBlocks={copy.body.inlineNote}
                />
              </>
            )}
          </PageSection>

          {/*
            The save is withheld while the body is one this editor cannot
            express — and the reason is stated above it rather than left to a
            disabled control with no written explanation.
          */}
          {parsed.unsupported ? null : (
            <FormActions
              saving={save.isPending}
              onReset={reset}
              saveLabel={sharedCopy.actions.save}
              resetLabel={sharedCopy.actions.reset}
            />
          )}
        </form>
      )}
    </>
  );
}
